import type { IBatchStore } from './batch-store-types';
import logger from '../../utils/logger';
import { timerKey } from '../../utils/timer-manager/timer-manager';
import { DeviceStatus } from '../../constants/DeviceStatus';
import { toNumber } from '../../utils/numbers';
import { OUTPUT_TTL } from '../../constants/Pac';
import {
  MeteredMaterialType,
  BatchAction,
  BatchMode,
} from '../../constants/MaterialModels';
import convertUnit from '../../utils/unit-converter';
import { isDischargeStartPercentCorrect } from './checkAdmixtureDischargeConditions';
import { checkPumpDischargeFinish } from './pump-discharge-operations';
import moment from 'moment';
import type { IMeteredDataToBatch } from '../../../types/batch/DataToBatch';
import type { IBatchData } from '../../containers/Batch/IBatchData';
import { autoCutOffAdjustmentForMetered } from './auto-cut-off-adjustment-for-metered';
import type {
  IPump,
  ITank,
} from '../../../types/device/DeviceModels';
import { reaction } from 'mobx';
import {
  getSecondWaterPump,
  getWaterPump,
} from './water-pump-checks';
import { waterShouldWaitForMoistureProbe } from './live-moisture';
import type { DischargeScale } from '../../../types/api/Discharge';
import type { MixDesignItem } from '../../../types/batch/MixDesignItem';

export const pumpDischargeMixin = {

  onPumpFinishFeed(
    this: IBatchStore,
    pumpData: IMeteredDataToBatch,
    data: IBatchData,
    initialPumpValue: number|null = null,
    onFinish: (pumpData: IMeteredDataToBatch, batchData: IBatchData) => void = () => {/* empty */},
  ) {

    const { pump, material, mft, headWaterActual = 0, washWaterActual = 0, extraWaterActual = 0 } = pumpData;
    const materialDetails = this.materialStore.materialDetails[material.materialDetailId];
    const materialCode = this.materialStore.getMaterialName(material);
    const meterRate = materialDetails.meterRate || 1;

    this.timers.set(timerKey.device(pump.id), () => {
      const { configuration } = this.settingsStore;
      const d = this.getDeviceStatus(pump.id);
      const weight = toNumber(headWaterActual + washWaterActual + extraWaterActual + ((this.getPumpWeight(pump) - (initialPumpValue || 0)) * meterRate), 2);
      const runningData = this.running.find(v => v.uid === data.uid && v.currentDrop === data.currentDrop);
      let target = toNumber(mft, 2);
      const tolerance = this.getTolerance(materialDetails, target);

      logger.log(`${materialCode}: tolerance: ${materialDetails.toleranceByPercent}% = ${tolerance?.over}`, target, weight);

      if (weight && weight > target + tolerance?.over) {
        logger.log(`${materialCode}: is over target! tolerance: ${materialDetails.toleranceByPercent}%, ${weight} > ${target} + ${tolerance?.over}`);

        if (!d.errorsOverridden.includes(DeviceStatus.OVER)) {
          const unit = pump.unitId ? this.getUnitById(pump.unitId) : materialDetails.itemBatchUnitSymbol;
          this.showOverTolerancePopup({
            material: materialCode,
            deviceId: pump.id,
            weight,
            target,
            unit,
            uid: data.uid,
            currentDrop: data.currentDrop,
          });
        }

        if (configuration.acknowledgeOverToleranceAdmixture && !d.errorsOverridden.includes(DeviceStatus.OVER)) {

          this.showError({ key: 'Weight is over target! Press Override button [F2]', props: { material: materialCode }});
          this.deviceStatus.set(pump.id, {
            ...d,
            weight,
            error: true,
            status: DeviceStatus.OVER,
          });

          this.onPumpFinishFeed(pumpData, data, initialPumpValue, onFinish);

          return;

        } else {
          this.showError({ key: 'Weight is over target!', props: { material: materialCode }});
        }
      }

      if (this.isHoldFeedDevice(pump.id)) {
        this.unHoldFeedDevice(pump.id);
      }

      autoCutOffAdjustmentForMetered(this.rootStore, pumpData, weight);

      this.deviceStatus.set(pump.id, {
        ...d,
        weight,
        error: false,
        status: DeviceStatus.WAIT_DISCH,
      });

      if (runningData?.mixDesign) {
        runningData.mixDesign.forEach((v: MixDesignItem) => {
          if ((v.batchCode && v.batchCode === material.batchCode) || v.constituentItemId === material.itemId) {
            let actualWeight = weight;
            let targetPerDrop = target;

            if (pump.unitId && pump.unitId !== materialDetails.itemBatchUnitId) {
              const pumpUnit = this.getUnitById(pump.unitId);
              actualWeight = toNumber(convertUnit(weight, pumpUnit, materialDetails.itemBatchUnitSymbol), 2);
              targetPerDrop = toNumber(convertUnit(target, pumpUnit, materialDetails.itemBatchUnitSymbol), 2);
            }

            v.actualWeight = actualWeight;
            v.actualPerDrop = actualWeight;
            v.targetPerDrop = targetPerDrop;
            logger.log('actual weight', materialCode, weight, actualWeight, targetPerDrop);
            this.saveResultForDrop(runningData, v);
          }
        });
      }

      if (pump.tankId) {
        const t = this.getDeviceStatus(pump.tankId);
        this.deviceStatus.set(pump.tankId, {
          ...t,
          weight,
          status: DeviceStatus.WAIT_DISCH,
        });
      }

      logger.log(`pump ${pump.description} finish feed:`, materialCode, weight);

      onFinish(pumpData, data);

    }, 2000);
  },

  checkSetMeterCommandSent(this: IBatchStore, pump: IPump, value: number) {

    if (this.isOutputOn(pump.gatePNo) || this.hasPumpFinished(pump)) {
      return;
    }

    this.timers.clear(timerKey.setMeterSent(pump.id));

    this.timers.set(timerKey.setMeterSent(pump.id), () => {
      const d = this.getDeviceStatus(pump.id);

      if (!this.isOutputOn(pump.gatePNo) && !this.hasPumpFinished(pump) && !this.isHoldFeedDevice(pump.id) && !d.weight) {
        this.setMeter(pump.meterPNo, value, pump.gatePNo);
        this.checkSetMeterCommandSent(pump, value);
      }

    }, 2000);
  },

  checkPumpFinishDischarge(
    this: IBatchStore,
    pumpData: IMeteredDataToBatch,
    data: IBatchData,
    onFinish: (pumpData?: IMeteredDataToBatch, batchData?: IBatchData) => void = () => {/* empty */},
  ) {

    if (this.isPumpEmpty(pumpData.pump)) {
      onFinish(pumpData, data);
      return;
    }

    reaction(
      () => this.statusCount,
      (counter, reaction) => {
        if (this.isAbort || !this.isActiveDrop(data)) {
          reaction.dispose();
        }

        if (this.isPumpEmpty(pumpData.pump)) {
          reaction.dispose();
          onFinish(pumpData, data);
        }
      },
    );
  },

  dischargeBottlePump(
    this: IBatchStore,
    pumpData: IMeteredDataToBatch,
    data: IBatchData,
    onFinish: () => void = () => {/* empty */},
  ) {

    if (this.isAbort || !this.isActiveDrop(data)) {
      return;
    }

    const { materialDetails, material, pump } = pumpData;

    if (materialDetails.type === MeteredMaterialType.Admixture
      && [
        BatchAction.FineAggStart,
        BatchAction.ScaleAggDischargeStart,
        BatchAction.ScaleCemDischargeStart,
        BatchAction.ScaleWaterDischargeStart,
      ].includes(materialDetails.dischargeStart)
      && materialDetails.dischargeStartPercent > 0
      && !isDischargeStartPercentCorrect(this.rootStore, data, pumpData)
    ) {
      logger.log('dischargeBottlePump dischargeStartPercent not ready:', material.code, pump.id);
      return;
    }

    if (materialDetails.type === MeteredMaterialType.Admixture
      && !this.isAdmixturePriorityReady(data, pumpData, true)
    ) {
      logger.log('discharge metered not ready, wait priority:', material.code, pump.id);
      return;
    }

    const dischargeDelay = materialDetails.dischargeDelay || 0;
    const lineClearTime = materialDetails.lineClearTime || 0;
    const d = this.getDeviceStatus(pump.id);

    if ([
      DeviceStatus.DISCH_DELAY,
      DeviceStatus.DISCHARGING,
      DeviceStatus.LINE_CLEAR,
    ].includes(d.status)) {
      return;
    }

    this.deviceStatus.set(pump.id, {
      ...d,
      status: DeviceStatus.DISCH_DELAY,
    });

    this.timers.set(timerKey.device(pump.id), () => {
      logger.log(`DISCHARGE BOTTLE #${pump.id}`, data.uid, data.currentDrop);
      this.setOutput(pumpData.pump.bottleGatePNo, OUTPUT_TTL);

      const d = this.getDeviceStatus(pump.id);
      this.deviceStatus.set(pump.id, {
        ...d,
        status: DeviceStatus.DISCHARGING,
      });

      this.checkPumpFinishDischarge(pumpData, data, () => {
        const d = this.getDeviceStatus(pump.id);
        this.deviceStatus.set(pump.id, {
          ...d,
          status: DeviceStatus.LINE_CLEAR,
        });

        this.timers.set(timerKey.device(pump.id), () => {
          this.setOutput(pump.bottleGatePNo, '0');

          const d = this.getDeviceStatus(pump.id);
          this.deviceStatus.set(pump.id, {
            ...d,
            status: DeviceStatus.FINISH,
          });

          onFinish();

          if (materialDetails.type === MeteredMaterialType.Admixture
            && materialDetails.priority
          ) {
            logger.log('trigger dischargeStart event, after admix with priority finish',
              material.code, materialDetails.priority, materialDetails.dischargeStart);
            this.onBatchAction(data, materialDetails.dischargeStart);
          }

          if (data.materialId) {
            if (data.mixerId) {
              this.dischargeMixer(data);
            } else {
              this.finishDischarge(data);
            }
          }
        }, lineClearTime * 1000);
      });

    }, dischargeDelay * 1000);
  },

  checkPumpIsEmpty(
    this: IBatchStore,
    pumpData: IMeteredDataToBatch,
    data: IBatchData,
    onEmpty: (pumpData: IMeteredDataToBatch, batchData: IBatchData) => void = () => {/* empty */},
  ) {

    if (this.isAbort || !this.isActiveDrop(data)) {
      return;
    }

    const { pump } = pumpData;

    if (!this.isBottlePump(pump)) {
      onEmpty(pumpData, data);
      return;
    }

    const d = this.getDeviceStatus(pump.id) || this.getDeviceStatusDefault(pump);
    let message = '';

    if (this.isPumpNotEmpty(pump) && !this.isOverride) {
      this.deviceStatus.set(pump.id, {
        ...d,
        status: DeviceStatus.NOT_EMPTY,
        error: true,
      });

      message = 'Pump is not empty, please press Override (F2)';
      this.showError(message);

      this.timers.clear(timerKey.device(pump.id));

      this.timers.set(timerKey.device(pump.id), () => {
        this.checkPumpIsEmpty(pumpData, data, onEmpty);
      }, 1000);

      return;
    }

    if (d.error) {
      this.deviceStatus.set(pump.id, {
        ...d,
        status: DeviceStatus.IDLE,
        error: false,
      });

      this.isOverride = false;
    }

    onEmpty(pumpData, data);
  },

  resumePrimaryScaleDischarge(this: IBatchStore, data: IBatchData) {

    const primary = data.sequence.discharges.find((v: DischargeScale) => v.isPrimary) || {};
    const primaryShouldResume = (!primary.withWater || primary.startAtHeadWater > 0) && primary.dischargeIsStop && !primary.dischargeFinish && !primary.dischargeIsResume;
    logger.log('on water head discharge finish:', primaryShouldResume, !primary.withWater, primary.dischargeIsStop, !primary.dischargeFinish, !primary.dischargeIsResume, primary);

    if (primaryShouldResume) {
      logger.log('discharge resume primary after water pause:', primary.id, primary.type);
      this.resumeDischargeScale(primary);
      primary.dischargeIsResume = true;
      this.dischargeScale(primary, data, () => {
        this.onPrimaryDischarge(primary, data);
      });
    }
  },

  isAdmixturePriorityReady(
    this: IBatchStore,
    data: IBatchData,
    pumpData: IMeteredDataToBatch,
    isDischarge: boolean = false,
  ): boolean {

    const { materialDetails, material, pump } = pumpData;
    const { batchStart, dischargeStart, priority } = materialDetails;
    const action = isDischarge ? dischargeStart : batchStart;

    const pumps = Object.keys(data.pumps).filter(pumpId => {
      const p = data.pumps[pumpId];
      const { materialDetails: md } = p;

      return md.type === MeteredMaterialType.Admixture
        && (isDischarge ? md.dischargeStart : md.batchStart) === action
        && (md.priority < priority && !p.isDischarged);
    });

    logger.log('admix priority:', pump.id, material.code, action, priority, 'wait for pumps:', pumps);

    return !pumps.length;
  },

  onFinishNonBottlePump(
    this: IBatchStore,
    pumpData: IMeteredDataToBatch,
    data: IBatchData,
    action: any,
  ) {

    const { pump, material, materialDetails } = pumpData;
    logger.log(`ON BATCH ACTION: NON BOTTLE #${pump.id} FINISH`, material.code, data.uid, data.currentDrop);
    const waterPump = getWaterPump(this.rootStore, data) || {};
    const secondWaterPump = getSecondWaterPump(this.rootStore, data) || {};
    const d = this.getDeviceStatus(pump.id);

    if (waterPump.id === pump.id || secondWaterPump.id === pump.id) {
      if (data.headWater && !pumpData.headWaterDischarged) {
        pumpData.headWaterActual = d.weight;

        if (waterShouldWaitForMoistureProbe(data, this)) {
          this.deviceStatus.set(pump.id, {
            ...d,
            status: DeviceStatus.WAIT_MOIST,
          });

          if (this.isPumpWithTank(pump)) {
            this.deviceStatus.set(pump.tankId, {
              ...this.getDeviceStatus(pump.tankId),
              uid: data.uid,
              tId: data.tId,
              currentDrop: data.currentDrop,
              status: DeviceStatus.WAIT_MOIST,
            });
          }

          return;

        } else {
          data.headWaterPumpDischarged = true;
          pumpData.headWaterDischarged = true;

          this.deviceStatus.set(pump.id, {
            ...d,
            status: DeviceStatus.WAIT_WATER,
          });
        }

        this.checkScalesIfWaitingForWater(data);

        if (!this.isPumpWithTank(pump)) {

          if (!pumpData.washWater) {
            pumpData.isDischarged = true;

            this.deviceStatus.set(pump.id, {
              ...d,
              status: DeviceStatus.FINISH,
            });

            this.checkScalesIfWaitingForWater(data);

          } else if (!data.finalScales && this.isDischarge) {
            this.discharge();
          }

          this.resumePrimaryScaleDischarge(data);
          return;
        }

      } else if (data.washWater && !data.washWaterPumpDischarged) {
        pumpData.washWaterActual = d.weight - pumpData.headWaterActual;

        if (waterShouldWaitForMoistureProbe(data, this)) {
          this.deviceStatus.set(pump.id, {
            ...d,
            status: DeviceStatus.WAIT_MOIST,
          });

          if (this.isPumpWithTank(pump)) {
            this.deviceStatus.set(pump.tankId, {
              ...this.getDeviceStatus(pump.tankId),
              uid: data.uid,
              tId: data.tId,
              currentDrop: data.currentDrop,
              status: DeviceStatus.WAIT_MOIST,
            });
          }

          return;

        } else {
          data.washWaterPumpDischarged = true;
          pumpData.washWaterDischarged = true;
        }
        logger.log('finish discharging wash water', material.code, data.washWater);
      } else if (pumpData.extraWater) {
        pumpData.extraWaterActual = d.weight - pumpData.headWaterActual - pumpData.washWaterActual;
      }
    }

    this.deviceStatus.set(pump.id, {
      ...d,
      status: (waterPump.id === pump.id || secondWaterPump.id === pump.id) && !data.washWaterPumpDischarged && data.washWater
        ? DeviceStatus.WAIT_WATER
        : DeviceStatus.FINISH,
    });

    if (this.isPumpWithTank(pump)) {
      const tankId = pump.tankId;

      this.deviceStatus.set(tankId, {
        ...this.getDeviceStatus(tankId),
        uid: data.uid,
        tId: data.tId,
        currentDrop: data.currentDrop,
        status: DeviceStatus.WAIT_DISCH,
      });

      if (!data.headWaterTankDischarged || data.batchActionFired.includes(materialDetails.dischargeStart)) {
        logger.log(`discharge tank #${tankId} after pump #${pump.id}`, material.code, data.uid, data.currentDrop);
        this.dischargeTank(tankId, pumpData, data, (data) => {

          if (waterPump.id === pump.id || secondWaterPump.id === pump.id) {
            if (data.headWater && data.headWaterPumpDischarged && !data.headWaterTankDischarged) {
              data.headWaterTankDischarged = true;
              this.resumePrimaryScaleDischarge(data);
            }

            if (!data.washWater || (data.washWater && data.washWaterPumpDischarged && !data.washWaterTankDischarged)) {
              data.pumps[pump.id].isDischarged = true;
              data.washWaterTankDischarged = true;
            }

            if (data.washWater && !data.washWaterTankDischarged && data.headWater && data.headWaterTankDischarged) {
              this.batchMetered(pumpData, data, null, () => {
                this.onFinishNonBottlePump(pumpData, data, action);
              });
            }
          } else {
            data.pumps[pump.id].isDischarged = true;
          }

          if (data?.materialId) {
            if (data.mixerId) {
              this.dischargeMixer(data);
            } else {
              this.finishDischarge(data);
            }
          }
        });
      }

    } else if (data.materialId) {
      if (data.mixerId) {
        this.dischargeMixer(data);
      } else {
        this.finishDischarge(data);
      }
    } else {
      data.pumps[pump.id].isDischarged = true;
    }
  },

  checkTankIsEmpty(
    this: IBatchStore,
    tank: ITank,
    pumpData: IMeteredDataToBatch,
    data: IBatchData,
    onEmpty: (tank: ITank) => void = () => {},
  ) {

    if (this.isAbort || !this.isActiveDrop(data)) {
      return;
    }

    const { pump } = pumpData;
    this.timers.clear(timerKey.tankEmpty(pump.id));

    const isEmpty = this.isTankEmpty(tank);
    const tankStatus = this.getDeviceStatus(tank.id);
    logger.log(`check tank #${tank.id} is empty:`, isEmpty, tankStatus.status, ', is override:', this.isOverride, data);

    if (isEmpty || (this.isOverride && tankStatus.status !== DeviceStatus.DISCHARGING)) {
      onEmpty(tank);
      return;
    }

    this.timers.set(timerKey.tankEmpty(pump.id), () => this.checkTankIsEmpty(tank, pumpData, data, onEmpty), 1000, `Check tank empty - pump #${pump.id}`);
  },

  checkTankIsClosed(
    this: IBatchStore,
    tank: ITank,
    data: IBatchData,
    onClose: (tank?: ITank) => void = () => {/* empty */},
  ) {

    if (this.isAbort || !this.isActiveDrop(data)) {
      return;
    }

    const isClosed = this.isTankClosed(tank);
    logger.log(`check tank #${tank.id} is closed:`, isClosed);

    if (isClosed || this.isOverride) {
      onClose(tank);
      return;
    }

    this.timers.set(timerKey.device(tank.id), () => this.checkTankIsClosed(tank, data, onClose), 1000, `Check tank closed - tank #${tank.id}`);
  },

  dischargeTank(
    this: IBatchStore,
    tankId: number,
    pumpData: IMeteredDataToBatch,
    data: IBatchData,
    onFinish: (data: IBatchData) => void = () => {/* empty */},
  ) {

    const tank = this.devicesStore.getDeviceById(tankId);
    const { pump } = pumpData;
    logger.log('discharge TANK', tankId, pump?.id);

    if (!tankId || !tank || !pump) {
      return;
    }

    this.timers.clear(timerKey.tankDischarge(pump.id));

    if (data.headWater && !data.headWaterTankDischarged && !this.isDischarge) {
      logger.log('head water not ready to discharge tank, waiting for discharge start', tankId);

      this.timers.set(timerKey.tankDischarge(pump.id), () => {
        this.dischargeTank(tankId, pumpData, data, onFinish);
      }, 2000);
      return;
    }

    if (data.headWater && !data.headWaterTankDischarged && data.mixerId && !this.mixerIsReadyForDischarge(data)) {
      logger.log('head water not ready to discharge tank, waiting for Mixer', tankId);

      this.timers.set(timerKey.tankDischarge(pump.id), () => {
        this.dischargeTank(tankId, pumpData, data, onFinish);
      }, 2000);
      return;
    }

    if (data.headWater && !data.headWaterTankDischarged && !this.isBootDeviceReadyForDischarge()) {
      logger.log('head water not ready to discharge tank, waiting for Boot', tankId);

      this.timers.set(timerKey.tankDischarge(pump.id), () => {
        this.dischargeTank(tankId, pumpData, data, onFinish);
      }, 2000);
      return;
    }

    if (data.headWater && !data.headWaterTankDischarged && !this.isDischargeAdmixBeforeHeadWaterCompleted(data)) {
      logger.log('head water not ready to discharge tank, waiting for admix');

      const conflictedAdmixWithWater = this.getConflictedAdmixWithWater(data, true, false);
      if (conflictedAdmixWithWater) {
        logger.warn(`discharge sequence conflict - water tank wait for admix ${conflictedAdmixWithWater.itemCode},
             forcing pump to discharge head water`);

        this.showError(`Discharge sequence conflict - Water is waiting for admix
            ${conflictedAdmixWithWater.itemCode} while ${conflictedAdmixWithWater.itemCode} is waiting for the water`);

      } else {
        this.timers.set(timerKey.tankDischarge(pump.id), () => {
          this.dischargeTank(tankId, pumpData, data, onFinish);
        }, 2000);
        return;
      }
    }

    if ((!data.headWater || data.headWaterTankDischarged) && data.washWater && !data.washWaterTankDischarged) {

      if (data.finalScales && !pumpData.washWaterReadyForDischarge && Object.keys(data.finalScales).length !== data.finalScalesDischarged.length) {
        logger.log('wash water not ready, waiting for scales to finish discharge or wash start %, tank', tankId, data);
        this.checkScalesIfWaitingForWater(data);

        this.timers.set(timerKey.tankDischarge(pump.id), () => {
          this.dischargeTank(tankId, pumpData, data, onFinish);
        }, 2000);
        return;
      }

      if (!this.isDischargeAdmixBeforeWashWaterCompleted(data)) {
        logger.log('tank wash water not ready, waiting for admix');

        const conflictedAdmixWithWater = this.getConflictedAdmixWithWater(data, false, true);
        if (conflictedAdmixWithWater) {
          logger.warn(`discharge sequence conflict - water tank wait for admix ${conflictedAdmixWithWater.itemCode},
             forcing pump to discharge wash water`);

          this.showError(`Discharge sequence conflict - Water is waiting for admix
            ${conflictedAdmixWithWater.itemCode} while ${conflictedAdmixWithWater.itemCode} is waiting for the water`);

        } else {
          this.timers.set(timerKey.tankDischarge(pump.id), () => {
            this.dischargeTank(tankId, pumpData, data, onFinish);
          }, 2000);
          return;
        }
      }

    }

    this.deviceStatus.set(tankId, {
      ...this.getDeviceStatus(tankId),
      status: DeviceStatus.DISCH_DELAY,
    });

    this.timers.clear(timerKey.tankDischargeDelay(pump.id));

    const dischargeDelay = (pumpData.materialDetails.dischargeDelay || 0) * 1000;

    this.timers.set(timerKey.tankDischargeDelay(pump.id), () => {
      this.setOutput(tank.gatePNo, OUTPUT_TTL);

      this.deviceStatus.set(tankId, {
        ...this.getDeviceStatus(tankId),
        status: DeviceStatus.DISCHARGING,
      });

      if (!data.headWaterTankDischarged && data.headWater) {
        this.onBatchAction(data, BatchAction.StartDischargingHeadWater);
      } else if (!data.washWaterTankDischarged && data.washWater) {
        this.onBatchAction(data, BatchAction.StartDischargingWashWater);
      }

      this.checkTankIsEmpty(tank, pumpData, data, () => {
        const emptyClearTime = (tank.emptyClearTime || 0) * 1000;

        this.deviceStatus.set(tankId, {
          ...this.getDeviceStatus(tankId),
          status: DeviceStatus.CLOSE_DELAY,
        });

        this.timers.set(timerKey.tankEmpty(pump.id), () => {
          this.setOutput(tank.gatePNo, '0');

          this.deviceStatus.set(tankId, {
            ...this.getDeviceStatus(tankId),
            status: DeviceStatus.CLOSING,
          });

          this.checkTankIsClosed(tank, data, () => {
            onFinish(data);

            this.deviceStatus.set(tankId, {
              ...this.getDeviceStatus(tankId),
              weight: 0,
              status: DeviceStatus.FINISH,
            });

            this.checkScalesIfWaitingForWater(data);

          });
        }, emptyClearTime);
      });

    }, dischargeDelay);

  },

  checkScalesIfWaitingForWater(this: IBatchStore, runningData: IBatchData) {

    runningData.sequence?.discharges.forEach((scale: DischargeScale) => {
      const s = this.getDeviceStatus(scale.id);

      if (scale.dischargeIsStop && s.status === DeviceStatus.WAIT_WATER) {
        logger.log('resume scale after water wait', scale.description, s.status, scale);

        this.resumeDischargeScale(scale);
        scale.dischargeIsResume = true;
        scale.dischargeStarted = true;

        this.dischargeScale(scale, runningData, () => {
          if (scale.isPrimary) {
            this.onPrimaryDischarge(scale, runningData);
          }

        });
      }
    });
  },

  checkPumpFinishFeed(
    this: IBatchStore,
    pumpData: IMeteredDataToBatch,
    data: IBatchData,
    onFinish: (pumpData: IMeteredDataToBatch, batchData: IBatchData) => void = () => {},
  ) {

    const { pump } = pumpData;

    if (this.hasPumpFinished(pump)) {
      this.resetMeter(pump.meterPNo);//1
      onFinish(pumpData, data);
      return;
    }

    reaction(
      () => this.statusCount,
      (counter, reaction) => {
        if (this.hasPumpFinished(pump)) {
          reaction.dispose();
          this.resetMeter(pump.meterPNo);//2
          onFinish(pumpData, data);
          return;
        }

        if (this.isAbort || !this.isActiveDrop(data) || this.isHoldFeed || data.isHoldLoad || this.isHoldFeedDevice(pump.id)) {
          reaction.dispose();
        }

      },
    );
  },

  checkCanDischargePump(
    this: IBatchStore,
    pumpData: IMeteredDataToBatch,
    data: IBatchData,
    onFinish: (pumpData: IMeteredDataToBatch, batchData: IBatchData) => void = () => {},
  ) {

    const d = this.getDeviceStatus(pumpData.pump.id);

    if (d.status === DeviceStatus.WAIT_DISCH && d.uid === data.uid && d.currentDrop === data.currentDrop) {
      onFinish(pumpData, data);
      return;
    }

    reaction(
      () => Array.from(this.deviceStatus.values()).find(v => v.id === pumpData.pump.id),
      (pumpStatus, reaction) => {
        if (this.isAbort || !this.isActiveDrop(data)) {
          reaction.dispose();
          return;
        }

        if (pumpStatus.status === DeviceStatus.WAIT_DISCH && pumpStatus.uid === data.uid && pumpStatus.currentDrop === data.currentDrop) {
          reaction.dispose();
          onFinish(pumpData, data);
        }
      },
    );
  },

  checkMeteredWeight(
    this: IBatchStore,
    pumpData: IMeteredDataToBatch,
    target: number,
    initialPumpValue: number|null,
    data: IBatchData,
    onHold: () => void = () => {},
    onFinish: () => void = () => {},
  ) {

    const { pump, material, headWater, headWaterActual = 0, washWaterActual = 0, extraWaterActual = 0 } = pumpData;
    const materialDetails = this.materialStore.materialDetails[material.materialDetailId];
    const minimumFlowRate = materialDetails.minimumFlowRate || 0;
    const meterRate = materialDetails.meterRate || 1;
    let startWeight = this.getPumpWeight(pump) - (initialPumpValue || 0);
    let startTime = moment();
    let endTime;
    let count = 0;
    let averageFlowRate = 0;
    const maxFlowRateProbes = materialDetails.flowRateTimeout * 5 || 50;

    reaction(
      () => this.statusCount,
      (counter, reaction) => {
        if (this.isAbort || !this.isActiveDrop(data)) {
          reaction.dispose();
          return;
        }

        const weight = this.getPumpWeight(pump) - (initialPumpValue || 0);
        const actualWeight = headWaterActual + washWaterActual + extraWaterActual + (weight * meterRate);
        const d = this.getDeviceStatus(pump.id);

        this.deviceStatus.set(pump.id, {
          ...d,
          weight: actualWeight,
        });

        if (pump.tankId) {
          const t = this.getDeviceStatus(pump.tankId);
          this.deviceStatus.set(pump.tankId, {
            ...t,
            weight: actualWeight,
          });
        }

        if (weight < startWeight) {
          this.deviceStatus.set(pump.id, {
            ...d,
            weight,
            status: DeviceStatus.METER_ERROR,
            error: true,
          });

          logger.log('pump meter error:', pump.description, weight, '<', startWeight);

          this.closePump(pump);
          onHold();
          reaction.dispose();

          return;
        }

        const primary = data.sequence.discharges.find((v: DischargeScale) => v.isPrimary);
        if (primary && headWater &&  primary.startAtHeadWater > 0 && !primary.dischargeIsResume) {
          const actualPercent = toNumber((actualWeight/headWater) * 100);
          logger.log('check percent for startAtHeadWater', primary.type, material.code, primary.startAtHeadWater, actualPercent);

          if (actualPercent >= primary.startAtHeadWater) {
            primary.startAtHeadWaterReady = true;
            this.updateScaleDischarge(primary, data);
            this.resumePrimaryScaleDischarge(data);
            logger.log('percent ok for startAtHeadWater, resume discharge scale', primary.type, actualPercent);
          }
        }

        if (
          this.isHoldFeed
          || data.isHoldLoad
          || this.isHoldFeedDevice(pump.id)
          || (this.isDirectFeedPump(pump, materialDetails) && this.isHoldDischarge)
        ) {
          this.holdPump(pump);
          onHold();
          reaction.dispose();

          return;
        }

        if (this.isFinishFeedDevice(pump.id)) {
          this.setMeter(pump.meterPNo, 0, pump.gatePNo);
          this.closePump(pump);
          onFinish();
          this.finishFeedDevices = this.finishFeedDevices.filter(id => id !== pump.id);
          reaction.dispose();

          return;
        }

        if (minimumFlowRate > 0 && materialDetails.batchMode === BatchMode.Auto) {
          endTime = moment();
          const diff = endTime.diff(startTime) / 1000;
          startTime = moment();

          const flowRate = (weight - startWeight) * (1 / diff);
          if (flowRate >= 0) {
            averageFlowRate += flowRate;
            count += 1;
          }

          const d = this.getDeviceStatus(pump.id);
          this.deviceStatus.set(pump.id, {
            ...d,
            flowRate: averageFlowRate / count,
          });

          // logger.log(`pump #${pump.id} flow rate:`, flowRate, 'time diff:', diff, 'min flow rate:', minimumFlowRate, 'weight diff:', weight - startWeight);

          if (count === maxFlowRateProbes) {
            logger.log(`pump #${pump.id} AVERAGE flow rate:`, averageFlowRate / count, 'min flow rate:', minimumFlowRate);

            if (averageFlowRate / count < minimumFlowRate) {
              this.showError({ key: 'Pump minimum flow rate drop below', props: { pumpId: pump.id, flowRate: minimumFlowRate, unit: d.unit }});

              this.onPausePlayFeedButtonPress(pump.id);
              this.holdPump(pump);
              onHold();

              this.deviceStatus.set(pump.id, {
                ...d,
                status: DeviceStatus.MIN_FLOW_RATE,
                error: true,
              });

              reaction.dispose();
              return;
            }

            count = 0;
            averageFlowRate = 0;
          }

          startWeight = weight;
        }

        checkPumpDischargeFinish(this, pump, weight, target, onFinish, reaction);
      },
    );

  },

};
